// External Imports
import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { getFormValues, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';

// PPUI Imports
import { Container } from '@paypalcorp/pp-react-grid';

// Internal Imports
import { BTN_FACTORY_V3_FORM } from '../../clientConstants';
import { validate } from '../../lib/clientValidatorV3';
import MailingAddressSwitch from '../../shared/switchPanels/MailingAddressSwitch';
import Note from './components/Note';
import StepHeader from './components/StepHeader';
import AdvancedUrls from '../../shared/AdvancedUrls';
import AdvancedVariables from './components/AdvancedVariables';
import WizardNavButtons from '../../shared/navigationButtons/WizardNavButtons';
import DraftErrorWarning from '../../shared/alerts/DraftErrorWarning';
import { FPTI_SPEC } from '../../utilsV3/fptiConstants';
import { trackEventWithData } from '../../utilsV3/analytics';
import _isEmpty from 'lodash/isEmpty';

const WizardStep4 = ({
  handleBack,
  handleSubmit,
  formValues,
  application,
  buttonFactoryV3,
  isDraft,
  handleSaveDraft,
  draftInfo,
  showDraftError,
  setHandleQuitForm,
  handleQuitDraftSave,
}) => {
  const userConsentData = application?.userConsentData;
  const trackPageLoadImpression = () => {
    trackEventWithData(
      FPTI_SPEC.ADVANCED_SETTINGS_PAGE_IM,
      userConsentData?.consentNeeded
        ? {
            step: 4,
            consentNeeded: userConsentData?.consentNeeded,
            userAgreementUrl: userConsentData?.userAgreementUrl,
            policyVersion: userConsentData?.policyVersion,
          }
        : { step: 4 }
    );
  };

  useEffect(() => {
    trackPageLoadImpression();
    setHandleQuitForm?.(() => handleSubmit(handleQuitDraftSave));
  }, []);

  const draftBusy = useSelector(
    (state) => state?.buttonFactoryV3?.draftBusy || false
  );

  if (_isEmpty(formValues)) {
    return null;
  }

  return (
    <>
      <Container className="wizard-step-container wizard-step-4-container">
        {showDraftError && <DraftErrorWarning />}
        <StepHeader />

        <form onSubmit={handleSubmit} className="wizard-form">
          <MailingAddressSwitch isChecked={formValues.noShipping === 'no'} />
          <Note
            isChecked={formValues.noNote === 'no'}
            showBadge={application.showNewFeatureBadge ?? false}
          />
          <AdvancedUrls noCancelReturn={formValues.noCancelReturn} />
          <div>
            <AdvancedVariables
              extraVariablesView={formValues.extraVariablesView}
              noAdvancedVariables={formValues.noAdvancedVariables}
            />
          </div>
        </form>
      </Container>
      <WizardNavButtons
        handleBack={handleBack}
        handleSubmit={handleSubmit}
        steps={{
          currentStep: buttonFactoryV3?.currentStep,
          totalSteps: buttonFactoryV3?.totalSteps,
        }}
        isButton={true}
        isDraft={isDraft}
        handleSaveDraft={handleSubmit(handleSaveDraft)}
        draftInfo={draftInfo}
        showDraftSpinner={draftBusy}
      />
    </>
  );
};

WizardStep4.propTypes = {
  buttonFactoryV3: PropTypes.object,
  handleBack: PropTypes.func,
  handleSubmit: PropTypes.func,
  formValues: PropTypes.object,
  onSubmit: PropTypes.func,
  application: PropTypes.object,
  handleSaveDraft: PropTypes.func,
  draftInfo: PropTypes.string,
  showDraftButton: PropTypes.bool,
  showDraftError: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const { application = {}, buttonFactoryV3 = {} } = state;

  return {
    application,
    buttonFactoryV3,
    formValues: getFormValues(BTN_FACTORY_V3_FORM)(state),
    initialValues: buttonFactoryV3.formattedButton,
  };
};

let WizardStep4Form = reduxForm({
  form: BTN_FACTORY_V3_FORM,
  keepDirtyOnReinitialize: true,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate,
})(WizardStep4);

WizardStep4Form = connect(mapStateToProps)(WizardStep4Form);

export default WizardStep4Form;
